import { Typography } from "@mui/material";
import { Breadcrumbs as BreadcrumbsMUI, Link } from "@mui/material";
import { projectPageStyles } from "../../pages/Projects/styles";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import { BreadcrumbsProps } from "./types";
import { useEffect, useState } from "react";
import PermMediaOutlinedIcon from "@mui/icons-material/PermMediaOutlined";

export const Breadcrumbs: React.FC<BreadcrumbsProps> = ({
  setOpenFoldersList,
  openFoldersList,
  handleRefreshTracks,
  setTracks,
  folders,
}) => {
  const { name, id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { projectId, folderIdFromNavigation } = location.state || {};
  const isAddTracksToFolderPage =
    window.location.pathname.includes("my-tracks") &&
    (folderIdFromNavigation || projectId);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const isHubPageAndFolderOpen = window.location.pathname.includes("undefined");
  const breadcrumbsStorageKey = `breadcrumbs_${id}`;
  const isHubPage = window.location.pathname.includes("my-tracks");

  useEffect(() => {
    if (!id || !setOpenFoldersList || initialLoadComplete) return;

    const loadSavedBreadcrumbs = () => {
      try {
        const savedBreadcrumbs = localStorage.getItem(breadcrumbsStorageKey);
        if (savedBreadcrumbs) {
          const parsedBreadcrumbs = JSON.parse(savedBreadcrumbs);
          if (
            Array.isArray(parsedBreadcrumbs) &&
            parsedBreadcrumbs.length > 0
          ) {
            console.log("Loading saved breadcrumbs:", parsedBreadcrumbs);
            setOpenFoldersList(parsedBreadcrumbs);
          }
        }
      } catch (e) {
        console.error("Error loading saved breadcrumbs:", e);
      }
      setInitialLoadComplete(true);
    };

    const pathParts = location.pathname.split("/");
    const isFolderPage = pathParts.includes("folder");
    const currentFolderId = isFolderPage
      ? pathParts[pathParts.indexOf("folder") + 1]
      : null;

    if (currentFolderId) {
      loadSavedBreadcrumbs();
    } else if (
      location.pathname === "/" ||
      location.pathname === "/my-tracks" ||
      !isFolderPage
    ) {
      if (!isAddTracksToFolderPage) {
        setOpenFoldersList([]);
        localStorage.removeItem(breadcrumbsStorageKey);
      }
      setInitialLoadComplete(true);
    }
  }, [
    id,
    setOpenFoldersList,
    breadcrumbsStorageKey,
    location.pathname,
    isAddTracksToFolderPage,
    initialLoadComplete,
  ]);

  useEffect(() => {
    if (id && openFoldersList && initialLoadComplete) {
      if (openFoldersList.length > 0) {
        console.log("Saving breadcrumbs:", openFoldersList);
        localStorage.setItem(
          breadcrumbsStorageKey,
          JSON.stringify(openFoldersList)
        );
      } else {
        localStorage.removeItem(breadcrumbsStorageKey);
      }
    }
  }, [openFoldersList, id, breadcrumbsStorageKey, initialLoadComplete]);

  const handleNavigation = (folderId: string) => {
    if (!openFoldersList || !setOpenFoldersList) return;
    const folderIndex = openFoldersList.findIndex(
      (folder) => folder.id === folderId
    );
    let updatedFolders;
    if (folderIndex !== -1) {
      updatedFolders = openFoldersList.slice(0, folderIndex + 1);
    } else {
      const folder = folders.find((f) => f.getId() === folderId);
      updatedFolders = folder
        ? [...openFoldersList, { id: folder.getId(), name: folder.getName() }]
        : openFoldersList;
    }
    setOpenFoldersList(updatedFolders);
    setTracks?.([]);

    navigate(
      `/project/${encodeURIComponent(
        name ?? "untitled"
      )}/${id}/folder/${folderId}`
    );
  };

  useEffect(() => {
    if (
      (location.pathname === "/" || location.pathname === "/my-tracks") &&
      !isAddTracksToFolderPage &&
      !isHubPageAndFolderOpen
    ) {
      setOpenFoldersList?.([]);
      setTracks?.([]);
      localStorage.removeItem(breadcrumbsStorageKey);
    }
  }, [
    location.pathname,
    setOpenFoldersList,
    setTracks,
    breadcrumbsStorageKey,
    isAddTracksToFolderPage,
    isHubPageAndFolderOpen,
  ]);

  return (
    <>
      <Typography sx={{ fontWeight: "bold" }} variant="h4">
        <BreadcrumbsMUI sx={{ gap: "10px" }} aria-label="breadcrumb">
          {isHubPageAndFolderOpen && (
            <Link
              underline="hover"
              color="inherit"
              onClick={() => navigate("/my-tracks")}
              sx={projectPageStyles.breadcrumbsLinks}
            >
              <HomeOutlinedIcon sx={{ fontSize: "21px", marginRight: "5px" }} />
              Hub
            </Link>
          )}
          {name !== "untitled" && !isHubPage && (
            <Link
              underline="hover"
              color="inherit"
              onClick={() => navigate(`/projects`)}
              sx={projectPageStyles.breadcrumbsLinks}
            >
              <PermMediaOutlinedIcon
                sx={{ fontSize: "21px", marginRight: "5px" }}
              />
              Projects
            </Link>
          )}
          {name !== "untitled" && (
            <Link
              underline="hover"
              color="inherit"
              onClick={() => {
                if (setOpenFoldersList) {
                  setOpenFoldersList([]);
                  handleRefreshTracks?.();
                }

                localStorage.removeItem(breadcrumbsStorageKey);

                navigate(
                  `/project/${encodeURIComponent(name || "unknown")}/${id || "unknown-id"}`
                );
              }}
              sx={projectPageStyles.breadcrumbsLinks}
            >
              {name}
            </Link>
          )}
          {openFoldersList?.map((folder, index) => {
            return index === openFoldersList.length - 1 ? (
              <Typography key={folder.id} sx={{ color: "text.primary" }}>
                {folder.name}
              </Typography>
            ) : (
              <Link
                key={folder.id}
                underline="hover"
                color="inherit"
                onClick={() => handleNavigation(folder.id)}
                sx={projectPageStyles.breadcrumbsLinks}
              >
                {folder.name}
              </Link>
            );
          })}
        </BreadcrumbsMUI>
      </Typography>
    </>
  );
};
